import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { findDOMNode } from "react-dom";
import { isSafari } from "react-device-detect";
import { useDebounce } from "usehooks-ts";

import screenfull from "screenfull";
import { frameToSmpte, smpteToFrame } from "framerate-utils";

import { anyNotInThreshold, clamp, frameRateFromFio, normalizeTimecode, off, on, parseNumber, secondsToMedia } from "./playerUtils";

type UsePlayerStateParams = {
	previewAssetInfo: any;
	frameAccurateTimingAvailable: boolean;
};

type LoopRangeState = {
	in: number | null;
	out: number | null;
	active: boolean;
};

const MIN_LOOP_DURATION_SECONDS = 0.05;

function sanitizeLoopPoint(value: any): number | null {
	const parsed = Number(value);

	if (!Number.isFinite(parsed) || parsed < 0) {
		return null;
	}

	return parsed;
}

function getDurationFromPreviewAssetInfo(previewAssetInfo: any): number {
	const assetDuration = parseNumber(previewAssetInfo?.duration);

	if (assetDuration !== null && assetDuration > 0) {
		return assetDuration;
	}

	const frames = parseNumber(previewAssetInfo?.frames);
	const fps = parseNumber(previewAssetInfo?.fps);

	if (frames !== null && frames > 0 && fps !== null && fps > 0) {
		return frames / fps;
	}

	return 0;
}

function getLoopPointClampMax(duration: number, previewAssetInfo: any): number {
	const playerDuration = parseNumber(duration);
	const assetDuration = getDurationFromPreviewAssetInfo(previewAssetInfo);

	return Math.max(playerDuration || 0, assetDuration || 0, 0);
}

function getMinimumLoopDuration(previewAssetInfo: any, frameAccurateTimingAvailable: boolean): number {
	const fps = parseNumber(previewAssetInfo?.fps);

	if (frameAccurateTimingAvailable && fps !== null && fps > 0) {
		return Math.max(1 / fps, 0.001);
	}

	return MIN_LOOP_DURATION_SECONDS;
}

function getLoopBoundaryThreshold(previewAssetInfo: any, frameAccurateTimingAvailable: boolean): number {
	const fps = parseNumber(previewAssetInfo?.fps);

	if (frameAccurateTimingAvailable && fps !== null && fps > 0) {
		return Math.max(0.5 / fps, 0.001);
	}

	return 0.02;
}

function getPreviewAssetLoopKey(previewAssetInfo: any) {
	return [previewAssetInfo?.fio_asset_id, previewAssetInfo?.id, previewAssetInfo?.asset_id, previewAssetInfo?.duration, previewAssetInfo?.frames, previewAssetInfo?.fps].map((value) => String(value ?? "")).join("|");
}


function isKeyboardShortcutIgnoredTarget(target: EventTarget | null) {
	const element = target as HTMLElement | null;

	if (!element) {
		return false;
	}

	const tagName = String(element.tagName || "").toLowerCase();

	return element.isContentEditable || tagName === "input" || tagName === "textarea" || tagName === "select";
}

function preventKeyboardShortcutDefault(evt: KeyboardEvent) {
	(document.activeElement as HTMLElement | null)?.blur?.();
	evt.stopPropagation();
	evt.preventDefault();
}

function useEventCallback<T extends (...args: any[]) => any>(callback: T): T {
	const callbackRef = useRef(callback);

	useEffect(() => {
		callbackRef.current = callback;
	}, [callback]);

	return useCallback(((...args: any[]) => callbackRef.current(...args)) as T, []);
}

export function usePlayerState({ previewAssetInfo, frameAccurateTimingAvailable }: UsePlayerStateParams) {
	const playerRef = useRef<any>();
	const playing = useRef<boolean>(false);

	const [seeking, setSeeking] = useState<boolean>(false);
	const [prefPlaying, setPrefPlaying] = useState<boolean>(true);
	const [progress, setProgress] = useState<any>({});
	const [fullscreenMode, setFullscreenMode] = useState(false);
	const [looping, setLooping] = useState<boolean>(true);
	const [loopInPoint, setLoopInPointState] = useState<number | null>(null);
	const [loopOutPoint, setLoopOutPointState] = useState<number | null>(null);
	const [muted, setMuted] = useState<boolean>(true);

	const [buffering, setBuffering] = useState<boolean>(true);

	const [lastPause, setLastPause] = useState<any>(undefined);
	const debouncedLastPause = useDebounce<any>(lastPause, 2000);

	const [fioTimingInvalid, setFioTimingInvalid] = useState(false);
	const [duration, setDuration] = useState<number>(0);
	const [curFrame, setCurFrame] = useState<number>(0);
	const [showTC, setShowTC] = useState<number>(0);

	const previewAssetLoopKey = useMemo(() => getPreviewAssetLoopKey(previewAssetInfo), [previewAssetInfo]);

	const loopRange = useMemo<LoopRangeState>(() => {
		const safeInPoint = sanitizeLoopPoint(loopInPoint);
		const safeOutPoint = sanitizeLoopPoint(loopOutPoint);
		const clampMax = getLoopPointClampMax(duration, previewAssetInfo);
		const minLoopDuration = getMinimumLoopDuration(previewAssetInfo, frameAccurateTimingAvailable);

		const clampedInPoint = safeInPoint === null ? null : clamp(safeInPoint, 0, clampMax > 0 ? clampMax : safeInPoint);
		const clampedOutPoint = safeOutPoint === null ? null : clamp(safeOutPoint, 0, clampMax > 0 ? clampMax : safeOutPoint);
		const active = clampedInPoint !== null && clampedOutPoint !== null && clampedOutPoint - clampedInPoint >= minLoopDuration;

		return {
			in: clampedInPoint,
			out: clampedOutPoint,
			active,
		};
	}, [duration, frameAccurateTimingAvailable, loopInPoint, loopOutPoint, previewAssetInfo]);

	const loopRangeActive = looping && loopRange.active;

	const toggleShowTC = useEventCallback(() => {
		const nextOption = showTC + 1;
		const frameRate = frameRateFromFio(previewAssetInfo?.fps);

		setShowTC(nextOption > 2 || (nextOption === 2 && typeof frameRate === "undefined") ? 0 : nextOption);
	});

	const getCurrentTimeSeconds = useEventCallback(() => {
		const player = playerRef.current?.getInternalPlayer?.();
		const currentTime = Number(player?.currentTime);

		if (Number.isFinite(currentTime) && currentTime >= 0) {
			return currentTime;
		}

		const currentDuration = Number(duration);
		const played = Number(progress?.played);

		if (Number.isFinite(currentDuration) && currentDuration > 0 && Number.isFinite(played) && played >= 0) {
			return currentDuration * played;
		}

		return 0;
	});

	const clampLoopPoint = useEventCallback((value: any) => {
		const parsedPoint = sanitizeLoopPoint(value);

		if (parsedPoint === null) {
			return null;
		}

		const clampMax = getLoopPointClampMax(duration, previewAssetInfo);

		return clamp(parsedPoint, 0, clampMax > 0 ? clampMax : parsedPoint);
	});

	const seekToTimeSeconds = useEventCallback((timeSeconds: number) => {
		const player = playerRef.current?.getInternalPlayer?.();

		if (!player) {
			return;
		}

		const clampMax = getLoopPointClampMax(duration || player?.duration || 0, previewAssetInfo);
		const safeTimeSeconds = clamp(Number.isFinite(timeSeconds) ? timeSeconds : 0, 0, clampMax > 0 ? Math.max(0, clampMax - 0.001) : Math.max(0, timeSeconds || 0));

		try {
			player.currentTime = safeTimeSeconds;
		} catch {}
	});

	const setLoopInPoint = useEventCallback((value: any = getCurrentTimeSeconds()) => {
		const nextInPoint = clampLoopPoint(value);

		setLoopInPointState(nextInPoint);

		if (nextInPoint !== null) {
			setLoopOutPointState((prevOutPoint) => {
				if (prevOutPoint === null) {
					return prevOutPoint;
				}

				const minLoopDuration = getMinimumLoopDuration(previewAssetInfo, frameAccurateTimingAvailable);

				return prevOutPoint - nextInPoint >= minLoopDuration ? prevOutPoint : null;
			});
		}
	});

	const setLoopOutPoint = useEventCallback((value: any = getCurrentTimeSeconds()) => {
		const nextOutPoint = clampLoopPoint(value);

		setLoopOutPointState(nextOutPoint);

		if (nextOutPoint !== null) {
			setLoopInPointState((prevInPoint) => {
				if (prevInPoint === null) {
					return prevInPoint;
				}

				const minLoopDuration = getMinimumLoopDuration(previewAssetInfo, frameAccurateTimingAvailable);

				return nextOutPoint - prevInPoint >= minLoopDuration ? prevInPoint : null;
			});
		}
	});

	const setLoopInToCurrentTime = useEventCallback(() => {
		setLoopInPoint(getCurrentTimeSeconds());
	});

	const setLoopOutToCurrentTime = useEventCallback(() => {
		setLoopOutPoint(getCurrentTimeSeconds());
	});

	const clearLoopPoints = useEventCallback(() => {
		setLoopInPointState(null);
		setLoopOutPointState(null);
	});

	const handleLoopPointKeyboardShortcuts = useEventCallback((evt: KeyboardEvent) => {
		if (!evt.shiftKey || evt.altKey || evt.ctrlKey || evt.metaKey) {
			return;
		}

		if (isKeyboardShortcutIgnoredTarget(evt.target)) {
			return;
		}

		const key = String(evt.key || "").toLowerCase();

		if (key === "i") {
			preventKeyboardShortcutDefault(evt);
			setLoopInToCurrentTime();
			return;
		}

		if (key === "o") {
			preventKeyboardShortcutDefault(evt);
			setLoopOutToCurrentTime();
			return;
		}

		if (key === "g") {
			preventKeyboardShortcutDefault(evt);
			clearLoopPoints();
		}
	});

	const play = useEventCallback(() => {
		setBuffering(false);
		playing.current = true;

		const playPromise = playerRef.current?.getInternalPlayer?.()?.play?.();

		if (playPromise?.catch) {
			playPromise.catch((e: any) => {
				console.log(e);
			});
		}

		setLastPause(undefined);
	});

	const pause = useEventCallback((forcedInput: any = false) => {
		const forced = typeof forcedInput === "boolean" ? forcedInput : false;

		if (prefPlaying && !forced) {
			return;
		}

		playing.current = false;
		playerRef.current?.getInternalPlayer?.()?.pause?.();
		setLastPause(Date.now());
	});

	const getLastFrame = useEventCallback(() => {
		return Math.max(1, previewAssetInfo?.frames || 1) - (isSafari ? 1 : 0);
	});

	const getCurFrame = useEventCallback(() => {
		const player = playerRef.current?.getInternalPlayer();

		if (!frameAccurateTimingAvailable || typeof player === "undefined" || player === null) {
			return -1;
		}

		let frame = Math.floor(player?.currentTime * previewAssetInfo?.fps) - 1 + (isSafari ? 1 : 0);

		const maxFrames = Math.max(0, previewAssetInfo.frames - (isSafari ? 1 : 0));

		if (frame < 0) {
			frame = 0;
		}

		if (frame > maxFrames) {
			frame = maxFrames;
		}

		return frame as number;
	});

	const goToFrame = useEventCallback((frame: any) => {
		const player = playerRef.current?.getInternalPlayer();

		if (typeof player === "undefined" || player === null || !frameAccurateTimingAvailable) {
			return;
		}

		const clampedFrame = clamp(frame, 0, getLastFrame());
		const getFramesInSec = ((clampedFrame + 1 - (isSafari ? 1 : 0)) / previewAssetInfo?.frames) * (previewAssetInfo?.frames / previewAssetInfo?.fps);

		player.currentTime = getFramesInSec + 0.01;
	});

	const handlePlay = useEventCallback(() => {
		if (loopRangeActive && loopRange.in !== null && loopRange.out !== null) {
			const currentTime = getCurrentTimeSeconds();
			const threshold = getLoopBoundaryThreshold(previewAssetInfo, frameAccurateTimingAvailable);

			if (currentTime < loopRange.in || currentTime >= loopRange.out - threshold) {
				seekToTimeSeconds(loopRange.in);
			}
		} else if (!fioTimingInvalid && frameAccurateTimingAvailable) {
			if (getCurFrame() === getLastFrame() - (isSafari ? 0 : 1)) {
				goToFrame(0);
			}
		}

		setPrefPlaying(true);
		play();
	});

	const handleToggleLoop = useEventCallback(() => {
		setLooping((prev) => !prev);
	});

	const handlePause = useEventCallback((prefPlayingInput: any = false) => {
		const nextPrefPlaying = typeof prefPlayingInput === "boolean" ? prefPlayingInput : false;

		setPrefPlaying(nextPrefPlaying);
		pause(true);
	});

	const resetAutoplayPreference = useEventCallback(() => {
		setPrefPlaying(true);
		setLastPause(undefined);
	});

	const handleOnReady = useEventCallback(() => {
		const playerDuration = playerRef.current?.getInternalPlayer()?.duration || 0;

		if (!frameAccurateTimingAvailable) {
			setFioTimingInvalid(true);
			setDuration(playerDuration);
			return;
		}

		const assetDuration = parseNumber(previewAssetInfo?.duration);
		const durationFromFrames = previewAssetInfo?.frames && previewAssetInfo?.fps ? previewAssetInfo.frames / previewAssetInfo.fps : null;

		const target = playerDuration;
		const durations = [assetDuration, durationFromFrames].filter((value) => Number.isFinite(value)) as number[];
		const threshold = 0.08;

		const timingInvalid = durations.length ? anyNotInThreshold(target, durations, threshold) : true;

		setFioTimingInvalid(timingInvalid);
		setDuration(timingInvalid ? target : durationFromFrames || assetDuration || target);
	});

	const moveFrame = useEventCallback((move: number) => {
		handlePause();
		setLastPause(undefined);

		if (fioTimingInvalid || !frameAccurateTimingAvailable) {
			try {
				playerRef.current.getInternalPlayer().currentTime = clamp(playerRef.current.getInternalPlayer().currentTime + move * 0.04, 0, duration - 0.05);
			} catch {}

			return;
		}

		goToFrame(getCurFrame() + move);
	});

	const getCurTC = useEventCallback(() => {
		if (fioTimingInvalid || !frameAccurateTimingAvailable) {
			return "00:00:00:00";
		}

		if (typeof previewAssetInfo === "undefined") {
			return "00:00:00:00";
		}

		const frameRate = frameRateFromFio(previewAssetInfo?.fps);

		if (typeof frameRate === "undefined") {
			return "Unkown Framerate";
		}

		const startTimecode = normalizeTimecode(previewAssetInfo?.metadata?.timecode || previewAssetInfo?.timecode);
		const startTCInFrames = smpteToFrame(frameRate, startTimecode);
		const currentFrame = startTCInFrames + getCurFrame();
		const currentTc = frameToSmpte(frameRate, currentFrame);

		return currentTc;
	});

	const getCurMedia = useEventCallback(() => {
		if (fioTimingInvalid || !frameAccurateTimingAvailable) {
			return secondsToMedia(playerRef.current?.getInternalPlayer()?.currentTime ?? 0);
		}

		if (typeof previewAssetInfo === "undefined") {
			return "00:00";
		}

		return secondsToMedia(getCurFrame() / previewAssetInfo?.fps);
	});

	const getLastMedia = useEventCallback(() => {
		if (fioTimingInvalid || !frameAccurateTimingAvailable) {
			return secondsToMedia(playerRef.current?.getInternalPlayer()?.duration || duration || 0);
		}

		if (typeof previewAssetInfo === "undefined") {
			return "00:00";
		}

		return secondsToMedia(getLastFrame() / previewAssetInfo?.fps);
	});

	const handleTogglePlayPause = useEventCallback(() => {
		if (playing.current) {
			handlePause();
		} else {
			handlePlay();
		}
	});

	const handleTCChange = useEventCallback(() => {
		setCurFrame(getCurFrame());
	});

	const handleProgress = useEventCallback(() => {
		handleTCChange();

		const player = playerRef.current?.getInternalPlayer?.();
		let currentTime = Number(player?.currentTime) || 0;
		const currentDuration = duration || Number(player?.duration) || 0;

		if (loopRangeActive && !seeking && playing.current && loopRange.in !== null && loopRange.out !== null) {
			const threshold = getLoopBoundaryThreshold(previewAssetInfo, frameAccurateTimingAvailable);

			if (currentTime >= loopRange.out - threshold) {
				seekToTimeSeconds(loopRange.in);
				currentTime = loopRange.in;
			}
		}

		setProgress({ played: currentDuration ? currentTime / currentDuration : 0 });
	});

	const handleSeek = useEventCallback((event: any, newValue: any | number[]) => {
		event.preventDefault();
		event.stopPropagation();
		setSeeking(true);

		if (playing.current) {
			handlePause(true);
		}

		let frame = newValue;
		const player = playerRef.current?.getInternalPlayer();

		if (!player) {
			setSeeking(false);
			return;
		}

		if (fioTimingInvalid || !frameAccurateTimingAvailable) {
			if (frame > duration) {
				frame = duration;
			}

			player.currentTime = frame;
		} else {
			if (frame > previewAssetInfo.frames) {
				frame = previewAssetInfo.frames;
			}

			const getFramesInSec = ((frame - (isSafari ? 1 : 0)) / previewAssetInfo?.frames) * (previewAssetInfo?.frames / previewAssetInfo?.fps);

			player.currentTime = getFramesInSec + 0.01;
		}
	});

	const handleSeekStop = useEventCallback((evt: any) => {
		const player = playerRef.current?.getInternalPlayer();

		evt.preventDefault();
		evt.stopPropagation();
		setSeeking(false);

		let shouldPause = false;

		if (fioTimingInvalid || !frameAccurateTimingAvailable) {
			if (player?.currentTime >= player?.duration) {
				shouldPause = true;
			}
		} else if (getCurFrame() + 1 >= getLastFrame()) {
			shouldPause = true;
		}

		if (shouldPause) {
			handlePause();
			return;
		}

		if (prefPlaying) {
			handlePlay();
		}
	});

	const onWebkitEndFullscreen = useEventCallback(() => {
		const player = playerRef.current?.getInternalPlayer();

		if (typeof player !== "undefined" && player !== null) {
			off(player, "webkitendfullscreen", onWebkitEndFullscreen);
		}

		setMuted(player?.muted ? true : false);
		handlePause();
	});

	const handleClickFullscreen = useEventCallback(() => {
		if (screenfull.isEnabled) {
			if (!screenfull.isFullscreen) {
				setFullscreenMode(true);

				const internalPlayer = playerRef.current?.getInternalPlayer?.();
				const domNode = findDOMNode(internalPlayer as any) || internalPlayer;

				screenfull.request(domNode as any);
			} else {
				setFullscreenMode(false);
				screenfull.exit();
			}
		} else {
			const player = playerRef.current?.getInternalPlayer();

			if (typeof player !== "undefined" && player !== null) {
				if (player.webkitEnterFullScreen) {
					player.webkitEnterFullScreen();
					on(player, "webkitendfullscreen", onWebkitEndFullscreen);
				}
			}
		}
	});

	const onFullScreenChange = useEventCallback(() => {
		setFullscreenMode(screenfull.isFullscreen);
		console.log("Am I fullscreen?", screenfull.isFullscreen ? "Yes" : "No");
	});

	const handleToggleMute = useEventCallback(() => {
		setMuted((prev) => !prev);
	});

	const handleEnded = useEventCallback(() => {
		if (loopRangeActive && loopRange.in !== null) {
			seekToTimeSeconds(loopRange.in);

			if (prefPlaying) {
				play();
			}

			return;
		}

		if (!looping) {
			handlePause();
		}
	});

	useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}

		window.addEventListener("keydown", handleLoopPointKeyboardShortcuts, true);

		return () => {
			window.removeEventListener("keydown", handleLoopPointKeyboardShortcuts, true);
		};
	}, [handleLoopPointKeyboardShortcuts]);

	useEffect(() => {
		clearLoopPoints();
	}, [clearLoopPoints, previewAssetLoopKey]);

	useEffect(() => {
		if (typeof lastPause === "undefined") {
			return;
		}

		if (fioTimingInvalid || !frameAccurateTimingAvailable) {
			return;
		}

		goToFrame(getCurFrame());
	}, [debouncedLastPause, fioTimingInvalid, frameAccurateTimingAvailable, goToFrame, getCurFrame, lastPause]);

	useEffect(() => {
		if (!screenfull.isEnabled) {
			return;
		}

		screenfull.on("change", onFullScreenChange);

		return () => {
			screenfull.off("change", onFullScreenChange);
		};
	}, [onFullScreenChange]);

	return useMemo(
		() => ({
			playerRef,
			playing,

			seeking,
			setSeeking,

			prefPlaying,
			setPrefPlaying,

			progress,
			setProgress,

			fullscreenMode,

			looping,
			setLooping,
			loopInPoint,
			loopOutPoint,
			loopRange,
			loopRangeActive,
			setLoopInPoint,
			setLoopOutPoint,
			setLoopInToCurrentTime,
			setLoopOutToCurrentTime,
			clearLoopPoints,

			muted,
			setMuted,

			buffering,
			setBuffering,

			fioTimingInvalid,
			setFioTimingInvalid,

			duration,
			setDuration,

			curFrame,
			setCurFrame,

			showTC,
			setShowTC,

			toggleShowTC,
			handlePlay,
			handlePause,
			resetAutoplayPreference,
			play,
			pause,
			moveFrame,
			getLastFrame,
			getCurFrame,
			getCurTC,
			getCurMedia,
			getLastMedia,
			handleTogglePlayPause,
			handleProgress,
			handleSeek,
			handleSeekStop,
			handleToggleMute,
			handleToggleLoop,
			handleClickFullscreen,
			handleOnReady,
			goToFrame,
			handleEnded,
		}),
		[
			seeking,
			prefPlaying,
			progress,
			fullscreenMode,
			looping,
			loopInPoint,
			loopOutPoint,
			loopRange,
			loopRangeActive,
			setLoopInPoint,
			setLoopOutPoint,
			setLoopInToCurrentTime,
			setLoopOutToCurrentTime,
			clearLoopPoints,
			muted,
			buffering,
			fioTimingInvalid,
			duration,
			curFrame,
			showTC,
			toggleShowTC,
			handlePlay,
			handlePause,
			resetAutoplayPreference,
			play,
			pause,
			moveFrame,
			getLastFrame,
			getCurFrame,
			getCurTC,
			getCurMedia,
			getLastMedia,
			handleTogglePlayPause,
			handleProgress,
			handleSeek,
			handleSeekStop,
			handleToggleMute,
			handleToggleLoop,
			handleClickFullscreen,
			handleOnReady,
			goToFrame,
			handleEnded,
		]
	);
}
